import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
  useRef,
} from 'react';
import { useAuth as useClerkAuth, useUser } from '@clerk/clerk-react';
import { useConvex } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { User } from '../types';
import { normalizeAppRole } from '../lib/portalRouting';

interface AuthContextType {
  user: User | null;
  role: User['role'];
  isManager: boolean;
  /** True once Clerk has finished loading; use for route guards. */
  isLoaded: boolean;
  /** True when Clerk has a session (use to avoid redirecting to login while user is still syncing). */
  isSignedIn: boolean;
  login: () => void;
  logout: () => Promise<void>;
  /** Optional setter for legacy/webdesigned auth panel; no-op when using Clerk. */
  setUser?: (user: User | null) => void;
  verification: {
    emailVerified: boolean;
    phoneVerified: boolean;
  };
}

const DEFAULT_ROLE: User['role'] = 'client';

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isLoaded, isSignedIn, signOut } = useClerkAuth();
  const { user: clerkUser } = useUser();
  const convex = useConvex();

  const [appUser, setAppUser] = useState<User | null>(null);
  const [role, setRole] = useState<User['role']>(DEFAULT_ROLE);
  const timeoutRef = useRef<number | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const idleMs = Number(import.meta.env.VITE_SESSION_IDLE_TIMEOUT_MS ?? 3600000);

  useEffect(() => {
    if (!isLoaded) return;
    if (!isSignedIn || !clerkUser) {
      setAppUser(null);
      setRole(DEFAULT_ROLE);
      return;
    }

    const derivedRole = normalizeAppRole(clerkUser.publicMetadata.role);

    const mapped: User = {
      id: clerkUser.id,
      email:
        clerkUser.primaryEmailAddress?.emailAddress ??
        clerkUser.emailAddresses[0]?.emailAddress ??
        '',
      firstName: clerkUser.firstName ?? '',
      lastName: clerkUser.lastName ?? '',
      phone: clerkUser.primaryPhoneNumber?.phoneNumber ?? '',
      role: derivedRole,
      preferences: { language: 'fr', notifications: true },
    };

    setAppUser(mapped);
    setRole(derivedRole);

    const syncUser = async () => {
      try {
        await convex.mutation(api.users.ensureUser, {
          clerkId: clerkUser.id,
          email: mapped.email,
          phone: mapped.phone,
          role: derivedRole,
          metadata: clerkUser.publicMetadata,
          lastActive: Date.now(),
        });
      } catch (e) {
        console.warn('Convex ensureUser failed', e);
      }
    };
    void syncUser();
  }, [clerkUser, convex, isLoaded, isSignedIn]);

  useEffect(() => {
    if (!isSignedIn) return;

    const resetTimer = () => {
      lastActivityRef.current = Date.now();
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = window.setTimeout(async () => {
        const now = Date.now();
        if (now - lastActivityRef.current >= idleMs) {
          try {
            await convex.mutation(api.users.touchActivity, {
              clerkId: clerkUser?.id ?? '',
              lastActive: now,
            });
          } catch (e) {
            console.warn('Convex touchActivity failed', e);
          }
          await signOut();
        }
      }, idleMs);
    };

    const events = ['mousemove', 'keydown', 'scroll', 'click'];
    events.forEach((evt) => window.addEventListener(evt, resetTimer));
    resetTimer();

    return () => {
      events.forEach((evt) => window.removeEventListener(evt, resetTimer));
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    };
  }, [convex, idleMs, isSignedIn, signOut, clerkUser]);

  const login = useCallback(() => {
    window.location.href = '/login';
  }, []);

  const setUserNoOp = useCallback((_: User | null) => {
    /* no-op: user is derived from Clerk */
  }, []);

  const logout = useCallback(async () => {
    await signOut();
  }, [signOut]);

  const verification = {
    emailVerified: Boolean(
      clerkUser?.primaryEmailAddress?.verification.status === 'verified'
    ),
    phoneVerified: Boolean(
      clerkUser?.primaryPhoneNumber?.verification.status === 'verified'
    ),
  };

  const value: AuthContextType = {
    user: appUser,
    role,
    isManager: role === 'manager',
    isLoaded,
    isSignedIn: Boolean(isSignedIn),
    login,
    logout,
    setUser: setUserNoOp,
    verification,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
